/**
 * Family & contacts — pure projection for the client information tile.
 *
 * Renders the ONE-015 `_relatedPerson` mirrors (OneTouch-canonical identity,
 * ADR-0025 Amendment 2026-07-24) as the tile's read-mostly contacts section.
 * Pure functions over plain doc data (no Firestore imports) so the bind-state
 * derivation and relationship labelling are unit-testable — same factoring as
 * the sibling narrative/monitored modules.
 *
 * Bind state is derived from the chain, not asserted:
 *   bound     — the ROLE `_person`'s uid slot is filled (they use the app)
 *   awaiting  — a phone is on file but no uid yet (zero-touch pending)
 *   no-number — no phone in OneTouch: cannot bind until the office adds one
 *               (in OneTouch — the mirror is read-only here by design)
 */

export interface FamilyContactSummary {
  /** `_relatedPerson` doc id — the handle office actions (revoke) act on. */
  docId?: string;
  name: string;
  /** Display labels, kinship first (the mirror stores kinship-first codes). */
  relationships: string[];
  phone?: string;
  email?: string;
  /** One-line address summary from `address[0]` (ONE-015 mirrors 562 of them). */
  address?: string;
  /** Circle of Support access, as the office sees it. See `accessState()`. */
  accessState: AccessState;
  /** A pending `_relatedPersonRequest` naming this contact — they signed in
   *  to the app and asked (see `applyPendingRequests`). */
  requestId?: string;
  /** ISO time they asked, when the request carries one. */
  requestedAt?: string;
  /** OMON-397: a REVOKED contact signed in again after the revocation (the
   *  bind refused them and stamped `_accessReasked`). ISO time of the last
   *  attempt; the office re-grants from the card, or doesn't. */
  reaskedAt?: string;
  displayRank: number;
  /** Storage path of the avatar thumbnail (ADR-0031), if a photo exists. */
  avatarPath?: string;
}

/** Relationship code → display label. Codes are the STC keys the ONE-015
 * mapper + office validator share; unknown codes render as themselves
 * (agent.fhir passes unrecognised strings through as STC codes). */
const RELATIONSHIP_LABELS: Record<string, string> = {
  "next-of-kin": "next of kin",
  "emergency-contact": "emergency contact",
  "key-holder": "key holder",
  "main-contact": "main contact",
  "personal-contact": "personal contact",
  "caregiver": "caregiver",
  "deputy": "deputy",
  "daughter": "daughter",
  "son": "son",
  "spouse": "spouse",
  "parent": "parent",
  "sibling": "sibling",
  "friend": "friend",
};

// agent.fhir stores three STC keys as standard FHIR v3-RoleClass codes
// (BOFF-029): mirrors carry NOK/ECON/CAREGIVER, not the STC key. Normalise
// before labelling or those chips render as raw "NOK"/"ECON".
const FHIR_CODE_TO_STC_KEY: Record<string, string> = {
  "NOK": "next-of-kin",
  "ECON": "emergency-contact",
  "CAREGIVER": "caregiver",
};

export function relationshipLabel(code: string): string {
  const key = FHIR_CODE_TO_STC_KEY[code] ?? code;
  return RELATIONSHIP_LABELS[key] ?? key;
}

/**
 * ADR-0031: `photo[0].url` is `gs://{bucket}/media/.../original.jpg` —
 * derivatives live beside it by convention. Returns the bucket-relative
 * storage path of the requested derivative, or undefined when the doc has
 * no usable photo Attachment.
 */
export function avatarStoragePath(
  doc: Record<string, unknown>,
  derivative: "original" | "thumb_256" | "thumb_1024" = "thumb_256",
): string | undefined {
  const photos = doc.photo;
  if (!Array.isArray(photos) || photos.length === 0) return undefined;
  const first = photos[0];
  if (!first || typeof first !== "object") return undefined;
  const url = (first as { url?: unknown }).url;
  if (typeof url !== "string" || !url.startsWith("gs://")) return undefined;
  const slash = url.indexOf("/", "gs://".length);
  if (slash < 0) return undefined;
  const path = url.slice(slash + 1);
  if (!path.startsWith("media/")) return undefined;
  return path.replace(/original\.jpg$/, `${derivative}.jpg`);
}

/** Extract the relationship codes from a `_relatedPerson` doc's
 * `relationship[]` CodeableConcepts, order preserved (kinship first). */
export function relationshipCodes(doc: Record<string, unknown>): string[] {
  const out: string[] = [];
  const rels = doc.relationship;
  if (!Array.isArray(rels)) return out;
  for (const concept of rels) {
    if (!concept || typeof concept !== "object") continue;
    const coding = (concept as { coding?: unknown }).coding;
    if (!Array.isArray(coding) || coding.length === 0) continue;
    const code = (coding[0] as { code?: unknown })?.code;
    if (typeof code === "string" && code && !out.includes(code)) out.push(code);
  }
  return out;
}

/** First usable phone value off `telecom[]`; template placeholders never
 * count (the same "unused"-is-not-data rule the bind matcher applies). */
export function contactPhone(doc: Record<string, unknown>): string | undefined {
  const telecom = doc.telecom;
  if (!Array.isArray(telecom)) return undefined;
  for (const entry of telecom) {
    if (!entry || typeof entry !== "object") continue;
    const { system, value } = entry as { system?: unknown; value?: unknown };
    if (system !== "phone") continue;
    if (typeof value === "string" && value.trim() && value !== "unused") {
      // ☠️ A phone is never an email. Found live 2026-09-03: a contact whose
      // telecom[0] was system:"phone" carrying ANOTHER PERSON'S email
      // address, which this function dutifully returned — so the tile showed
      // one nurse's name above a different nurse's email, and the app-access
      // line promised to "verify this number".
      //
      // The system label was wrong and the value was right there to check.
      // Trusting the label over the value is what let it through, so the
      // guard belongs here at the boundary as well as in the sync that
      // wrote it: a mislabelled entry must not be able to put a stranger's
      // contact details under someone's name.
      if (value.includes("@")) continue;
      return value.trim();
    }
  }
  return undefined;
}

/** First usable email off `telecom[]` — same placeholder rule as phones. */
export function contactEmail(doc: Record<string, unknown>): string | undefined {
  const telecom = doc.telecom;
  if (!Array.isArray(telecom)) return undefined;
  for (const entry of telecom) {
    if (!entry || typeof entry !== "object") continue;
    const { system, value } = entry as { system?: unknown; value?: unknown };
    if (system !== "email") continue;
    if (typeof value === "string" && value.trim() && value !== "unused") {
      return value.trim();
    }
  }
  return undefined;
}

/** One-line summary of `address[0]` (line + city + postalCode). */
export function contactAddress(doc: Record<string, unknown>): string | undefined {
  const addresses = doc.address;
  if (!Array.isArray(addresses) || !addresses[0] || typeof addresses[0] !== "object") return undefined;
  const a = addresses[0] as { line?: unknown; city?: unknown; postalCode?: unknown };
  // ☠️ "unused" is the template's filler and must be stripped from EVERY
  // part, not just `line`. It was filtered on line only, so a contact whose
  // city and postcode were both placeholders rendered the address as
  // literally "unused, unused" (seen live 2026-09-03). One shared predicate
  // now, so the three fields cannot drift apart again.
  const usable = (v: unknown): v is string =>
    typeof v === "string" && v.trim() !== "" && v.trim() !== "unused";
  const lines = Array.isArray(a.line) ? a.line.filter(usable) : [];
  const parts = [...lines, a.city, a.postalCode]
    .filter(usable)
    .map((s) => s.trim());
  return parts.length ? parts.join(", ") : undefined;
}

/** Display name from `name[0]`, filtering the template's "unused" filler. */
export function contactName(doc: Record<string, unknown>): string {
  const names = doc.name;
  if (Array.isArray(names) && names[0] && typeof names[0] === "object") {
    const entry = names[0] as { given?: unknown; family?: unknown };
    const given = Array.isArray(entry.given)
      ? entry.given.filter((g): g is string => typeof g === "string" && g !== "unused")
      : [];
    const family = typeof entry.family === "string" ? entry.family : "";
    const text = [...given, family].filter(Boolean).join(" ").trim();
    if (text) return text;
  }
  return "(unnamed contact)";
}

/**
 * Circle of Support access for one contact — the office's view.
 *
 * ☠️ ADR-0045 amendment 2026-09-21: THE OFFICE IS THE ONLY GATE. A phone
 * number on file used to mean "ready to join, no invite needed", because the
 * sign-in admitted any matching number. From that date a match without an
 * office grant lands in the request queue instead, so this model follows the
 * server's rule (`family_onboarding.has_access`) exactly:
 *
 *   - "revoked"     the office removed access. Checked FIRST, as the server
 *                   does — the old three-state model had no such state, so a
 *                   revoked contact with a number on file wore "◍ ready".
 *   - "using"       signed in to the app (the ROLE person carries their uid).
 *   - "granted"     the office said yes; they have not signed in yet.
 *   - "not-granted" a contact, but the office has not decided.
 *
 * A missing mobile number is NOT a state: the office can grant regardless,
 * and the card says what they still need to sign in.
 */
export type AccessState = "using" | "granted" | "not-granted" | "requested" | "revoked";

/** One pending `_relatedPersonRequest`, reduced to what the card needs. */
export interface PendingRequest {
  id: string;
  matchedRelatedPersonDocIds: string[];
  requestedAt?: string;
}

/**
 * Pure. Mark contacts who have ASKED (2026-10-24): a contact with no access
 * whom a pending request names becomes "requested", so the office sees the
 * ask where it looks. The grant itself finds the request server-side (by
 * contact), closes it and tells the person who asked. Granted, using and
 * revoked contacts are untouched: a revoked number is refused at sign-in,
 * never queued.
 */
export function applyPendingRequests(
  contacts: FamilyContactSummary[],
  requests: PendingRequest[],
): FamilyContactSummary[] {
  return contacts.map((c) => {
    if (c.accessState !== "not-granted" || !c.docId) return c;
    const req = requests.find((r) => r.matchedRelatedPersonDocIds.includes(c.docId!));
    return req
      ? { ...c, accessState: "requested", requestId: req.id, requestedAt: req.requestedAt }
      : c;
  });
}

/** Mirrors `family_onboarding.is_granted`: fails CLOSED — only an explicit
 *  `{granted: true}` or a bare `true` is a grant. */
export function isGranted(doc: Record<string, unknown>): boolean {
  const v = doc._accessGranted;
  if (v && typeof v === "object") return (v as { granted?: unknown }).granted === true;
  return v === true;
}

/** Mirrors `family_onboarding.is_revoked`: anything truthy means revoked. */
export function isRevoked(doc: Record<string, unknown>): boolean {
  const v = doc._accessRevoked;
  if (v && typeof v === "object") return Boolean((v as { revoked?: unknown }).revoked);
  return Boolean(v);
}

export function accessState(doc: Record<string, unknown>, roleUid: string | null): AccessState {
  if (isRevoked(doc)) return "revoked";
  if (typeof roleUid === "string" && roleUid.trim()) return "using";
  return isGranted(doc) ? "granted" : "not-granted";
}

/** ISO time at which a revoked contact last signed in again — only when
 * that attempt came AFTER the current revocation (an older re-ask, before a
 * re-grant and a second revoke, is history). Mirrors the server's
 * `reasked_after_revocation` (lib/family_onboarding.py). */
export function reaskedAfterRevocation(doc: Record<string, unknown>): string | undefined {
  if (!isRevoked(doc)) return undefined;
  const reask = doc._accessReasked;
  if (!reask || typeof reask !== "object") return undefined;
  const at = stampIso((reask as { at?: unknown }).at);
  if (!at) return undefined;
  const revoked = doc._accessRevoked;
  const revokedAt = revoked && typeof revoked === "object"
    ? stampIso((revoked as { at?: unknown }).at) : undefined;
  if (revokedAt && at < revokedAt) return undefined;
  return at;
}

/** A Firestore Timestamp, a Date or an ISO string → ISO string (else undefined). */
function stampIso(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toISOString();
  if (v && typeof v === "object" && typeof (v as { toDate?: unknown }).toDate === "function") {
    return (v as { toDate: () => Date }).toDate().toISOString();
  }
  return undefined;
}

/**
 * Project one mirror + its resolved ROLE-person uid into the tile row model.
 * `roleUid` is the ROLE `_person`'s `identifier.googleIdentityPlatform.value`
 * (resolved by the loader; null when the chain didn't resolve — treated as
 * unbound, never as an error state the family should wear).
 */
export function projectFamilyContact(
  doc: Record<string, unknown>,
  roleUid: string | null,
  docId?: string,
): FamilyContactSummary {
  const phone = contactPhone(doc);
  const rank = doc._displayRank;
  return {
    docId,
    name: contactName(doc),
    relationships: relationshipCodes(doc).map(relationshipLabel),
    phone,
    email: contactEmail(doc),
    address: contactAddress(doc),
    accessState: accessState(doc, roleUid),
    reaskedAt: reaskedAfterRevocation(doc),
    displayRank: typeof rank === "number" ? rank : 0,
    avatarPath: avatarStoragePath(doc),
  };
}

/** Sort: rank ascending (0 = unranked sinks last), then name. */
export function sortFamilyContacts(contacts: FamilyContactSummary[]): FamilyContactSummary[] {
  return [...contacts].sort((a, b) => {
    const ra = a.displayRank || Number.MAX_SAFE_INTEGER;
    const rb = b.displayRank || Number.MAX_SAFE_INTEGER;
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
}
